import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import styles from './SimpleVideoPlayer.module.scss';
import { clsx } from 'clsx';
import Image, { ImageProps } from '@/src/shared/components/Image';
import LoadingOverlay from './LoadingOverlay';
import { playVideoWithErrorHandler } from '@/src/shared/utils/video';

const VideoPlayButton = dynamic(
  () => import('@/src/shared/components/VideoPlayButton')
);

type HtmlVideoProps = React.HTMLProps<HTMLVideoElement>;

type Props = Omit<HtmlVideoProps, 'ref'> & {
  disableContextMenu?: boolean;
  disablePictureInPicture?: boolean;
  controlsList?: string;
  showPlayButton?: boolean;
  playButtonColor?: 'white' | 'blue';
  play?: boolean;
  onPlayChange?: (newValue: boolean) => void;
  classNameContainer?: string;
  imgProps?: Omit<ImageProps, 'src' | 'fill' | 'alt'>;
  classes?: { thumbnail?: string };
  displayVideoLoading?: boolean;
};

export function SimpleVideoPlayer({
  disableContextMenu,
  onContextMenu,
  disablePictureInPicture,
  controlsList,
  autoPlay,
  children,
  showPlayButton,
  onClick,
  controls,
  play,
  onPlayChange,
  playButtonColor,
  poster,
  imgProps,
  classes,
  displayVideoLoading = false,
  ...props
}: Props) {
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>();
  const [isPlaying, setIsPlaying] = useState(false);
  const { classNameContainer, ...rest } = props;

  const handlePlayChange = () => {
    if (onPlayChange) onPlayChange(isPlaying);
  };

  const toggleVideo = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    if (video.paused) {
      void playVideoWithErrorHandler(video);
      video.muted = false;
      video.volume = 1;
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
    handlePlayChange();
  };

  const handleClick: React.MouseEventHandler<HTMLVideoElement> = (event) => {
    if (showPlayButton) toggleVideo();
    if (onClick) onClick(event);
  };

  // auto play effect
  useEffect(() => {
    if (!autoPlay || isPlaying || play === true) return;
    const playInterval = setInterval(() => {
      if (videoRef.current?.paused) {
        void playVideoWithErrorHandler(videoRef.current);
        setIsPlaying(true);
        clearInterval(playInterval);
      }
    }, 2000);
    return () => clearInterval(playInterval);
  }, [autoPlay, isPlaying, play]);

  // play effect
  useEffect(() => {
    if (typeof play === 'undefined') return;
    if (play && isPlaying) return;
    if (play && videoRef.current?.paused) {
      void playVideoWithErrorHandler(videoRef.current);
      setIsPlaying(true);
    } else {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  }, [play, isPlaying]);

  const handleContextMenu: React.MouseEventHandler<HTMLVideoElement> = (
    event
  ) => {
    if (disableContextMenu) {
      event.preventDefault();
    }
    if (onContextMenu) onContextMenu(event);
  };

  const video = (
    <>
      <video
        {...rest}
        ref={videoRef}
        autoPlay={autoPlay}
        onContextMenu={handleContextMenu}
        disablePictureInPicture={disablePictureInPicture}
        controlsList={controlsList}
        onClick={handleClick}
        controls={showPlayButton && !isPlaying ? false : controls}
        onLoadStart={() => setLoading(true)}
        onLoadedData={() => setLoading(false)}
      >
        {children}
        Sorry, your browser doesn&apos;t support embedded videos.
      </video>
      {displayVideoLoading && isPlaying && loading && <LoadingOverlay />}
      {!isPlaying && imgProps && (
        <div className={clsx(styles.thumbnail, classes?.thumbnail)}>
          <Image src={poster} alt="" fill {...imgProps} />
        </div>
      )}
    </>
  );

  if (showPlayButton) {
    return (
      <div className={clsx(styles.container, classNameContainer)}>
        {props.src && showPlayButton && !isPlaying && (
          <button className={styles.playButtonWrapper} onClick={toggleVideo}>
            <VideoPlayButton color={playButtonColor} />
          </button>
        )}
        {video}
      </div>
    );
  }
  return <>{video}</>;
}
